import React, { useState } from 'react'
import { DateTime } from 'luxon'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import StepDescription from '../step-description'
import LessonTypeCard from '../LessonTypeCard'
import TimeSlotSelect from './time-slot-select'
import Error from 'components/error'
import SelectTimeSkeleton from './select-time-skeleton'
import RequestTimeForm from '../RequestTimeForm'
import { useMonthlyLessonAvailability } from './utils'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import ArrowBackOutlinedIcon from '@material-ui/icons/ArrowBackOutlined'
import CoachCardSm from 'components/coach-card-small'
import StepDescriptionMobile from '../step-description-mobile'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'

import { useStyles } from './styles'
import { get } from 'lodash'
import VerifyAccessCode from 'components/verify-access-code'

const SelectTimeLayout = ({
  lessonTypes,
  currentUrl,
  previousUrl,
  refetch,
  setShowRequestTime,
  lessonCreditBalanceQuantity,
}) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const [selectedDate, setSelectedDate] = useState(DateTime.local())
  const coach = lessonTypes[0].coach
  const title = coach?.coachProfile?.title
  const profilePhoto = coach?.coachProfile?.profilePhoto
  const requiresAccessCode = get(lessonTypes, '0.requiresAccessCode')
  const location = useLocation()
  const [verifiedAccessCode, setVerifiedAccessCode] = useState(
    !requiresAccessCode || location?.state?.verifiedAccessCode,
  )
  const [showTimezone] = useFeatureFlags([flags.FLAG_TIMEZONE_EVENT_BOOKING])
  const description = showTimezone
    ? 'Find a day and time that works best for your schedule below. The available times are affiliated with the time zone of the facility.'
    : 'Find a day and time that works best for your schedule below.'

  const classes = useStyles()
  const { returnTo } = qs.parse(location.search)
  const url =
    location.search.includes('returnTo') &&
    !location.search.includes('startDateTime')
      ? returnTo
      : previousUrl

  return (
    <Container style={{ marginTop: '60px' }}>
      <Grid container spacing={2} direction={isMobile ? 'row' : 'row-reverse'}>
        <Grid item md={3} xs={12}>
          <StepDescriptionMobile
            stepText="Step 1 of 3"
            previousUrl={previousUrl}
          />
          <CoachCardSm
            coach={{
              name: coach.name,
              coachAvatar: profilePhoto,
              facilityName: title,
            }}
          />
          <LessonTypeCard
            lessonInfo={lessonTypes.length && lessonTypes[0]}
            lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Box flexGrow={1} display={{ xs: 'none', md: 'flex' }}>
            <Button
              href={url}
              rel="noopener noreferrer"
              startIcon={<ArrowBackOutlinedIcon />}
            >
              BACK
            </Button>
          </Box>
          <StepDescription
            stepText="Step 1 of 3"
            title="Choose a day and time"
            description={description}
          />
          <Grid container item spacing={3} className={classes.timeSlotStyles}>
            <TimeSlotSelect
              data={lessonTypes}
              coach={lessonTypes[0].coach}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              currentUrl={currentUrl}
              refetch={refetch}
              setShowRequestTime={setShowRequestTime}
            />
          </Grid>
        </Grid>
      </Grid>
      <VerifyAccessCode
        lessonType={lessonTypes[0]}
        isOpen={!verifiedAccessCode && !lessonCreditBalanceQuantity}
        setIsOpen={setVerifiedAccessCode}
        coachName={coach.name}
        setVerifiedAccessCode={setVerifiedAccessCode}
      />
    </Container>
  )
}

export default ({
  currentUrl,
  previousUrl,
  locationId,
  lessonTypeId,
  lessonCreditBalanceQuantity,
}) => {
  const [flagCanRequestTime] = useFeatureFlags([flags.FLAG_FEAT_REQUEST_TIME])
  const { data, loading, error, refetch } = useMonthlyLessonAvailability(
    locationId,
    lessonTypeId,
  )
  const [showRequestTime, setShowRequestTime] = useState(false)

  if (error) return <Error error={error} />
  if (loading) return <SelectTimeSkeleton />

  const { lessonTypes } = data
  const coach = lessonTypes[0].coach

  if (showRequestTime) {
    return (
      <RequestTimeForm
        coach={coach}
        goBackLabel="Back"
        goBackHandler={() => {
          setShowRequestTime(false)
        }}
        source="MYPGA_SCHEDULE_FULLY_BOOKED"
      />
    )
  }

  return (
    <SelectTimeLayout
      lessonTypes={lessonTypes}
      currentUrl={currentUrl}
      previousUrl={previousUrl}
      refetch={refetch}
      setShowRequestTime={
        flagCanRequestTime && coach.coachProfile ? setShowRequestTime : false
      }
      lessonCreditBalanceQuantity={lessonCreditBalanceQuantity}
    />
  )
}
